/**
 * lib/sample-child.ts
 *
 * A dummy child shown to a parent who has no linked child yet, so they can
 * see what the app will show them before they link one. It is a normal
 * `ParentStudentLink` with the reserved id `sample`, so the real child
 * screens (hub, reports, report, homework, result, activity) render it
 * unchanged — `lib/api.ts` answers the per-child reads for this id from the
 * data below instead of the server, and nothing is ever written.
 *
 * Only offered while the parent has no approved or pending link
 * (`isSampleAvailable`); once a real child exists, `/children/sample`
 * resolves to nothing and the pages redirect as for any unknown link.
 */

import type {
  AssignmentResultResponse,
  AssignmentSummary,
  ChildActivityResponse,
  ChildSummaryHistoryItem,
  ChildSummaryHistoryResponse,
  ParentStudentLink,
} from '@tupai/types';
import type { ChildSummaryResponse } from '@/lib/api';
import { getLanguage, i18n, type AppLocale } from '@/lib/language';

export const SAMPLE_LINK_ID = 'sample';
const SAMPLE_PREFIX = 'sample-';

export const isSampleLink = (linkId: string | null | undefined): boolean =>
  linkId === SAMPLE_LINK_ID;

/** Report and assignment ids of the sample child all carry this prefix. */
export const isSampleId = (id: string | null | undefined): boolean =>
  !!id && id.startsWith(SAMPLE_PREFIX);

/** Same test ChildrenPage uses for its empty state: nothing approved or pending. */
export const isSampleAvailable = (links: ParentStudentLink[]): boolean =>
  !links.some((l) => l.status === 'approved' || l.status === 'pending');

const CHILD_NAME = 'Sample Child';
const DAY = 86_400_000;

// Relative to "now" so the activity feed's TODAY / YESTERDAY groups and the
// report dates always read as recent.
const ago = (days: number, hour = 20, minute = 0): string => {
  const d = new Date(Date.now() - days * DAY);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
};

export const SAMPLE_LINK: ParentStudentLink = {
  link_id: SAMPLE_LINK_ID,
  status: 'approved',
  initiated_by: 'parent',
  other_party_name: CHILD_NAME,
  other_party_phone: '',
  // Server-formatted the same way (`formatGrade`), which is English-only.
  other_party_detail: 'KSSM · Form 2',
  student_syllabus: 'KSSM',
  student_chapters_complete: 4,
  student_chapters_in_progress: 2,
  student_chapters_total: 13,
  student_avatar: 'octopus',
  student_daily_avg_mins: 22,
  student_accuracy: 78,
  requested_at: ago(30),
  resolved_at: ago(30),
  needs_my_action: false,
};

type SampleTitleKey = `sampleReportTitle${1 | 2 | 3 | 4 | 5}`;

const CHAPTERS = {
  fractions: { id: 'sample-ch-fractions', en: 'Fractions', ms: 'Pecahan', num: 3 },
  ratios: { id: 'sample-ch-ratios', en: 'Ratios and rates', ms: 'Nisbah dan kadar', num: 8 },
  algebra: {
    id: 'sample-ch-algebra',
    en: 'Algebraic expressions',
    ms: 'Ungkapan algebra',
    num: 5,
  },
  percentages: { id: 'sample-ch-percentages', en: 'Percentages', ms: 'Peratus', num: 7 },
} as const;

// ── Reports ─────────────────────────────────────────────────────────────────

const REPORTS: {
  id: string;
  days: number;
  accuracy: number;
  read: boolean;
  titleKey: SampleTitleKey;
}[] = [
  { id: 'sample-r1', days: 2, accuracy: 78, read: false, titleKey: 'sampleReportTitle1' },
  { id: 'sample-r2', days: 9, accuracy: 71, read: true, titleKey: 'sampleReportTitle2' },
  { id: 'sample-r3', days: 16, accuracy: 74, read: true, titleKey: 'sampleReportTitle3' },
  { id: 'sample-r4', days: 23, accuracy: 69, read: true, titleKey: 'sampleReportTitle4' },
  { id: 'sample-r5', days: 30, accuracy: 66, read: true, titleKey: 'sampleReportTitle5' },
];

function historyItem(r: (typeof REPORTS)[number], locale: AppLocale): ChildSummaryHistoryItem {
  return {
    id: r.id,
    title: i18n.getFixedT(locale, 'hub')(r.titleKey),
    generated_at: ago(r.days, 9),
    period_start: ago(r.days + 6, 0),
    period_end: ago(r.days, 23, 59),
    period_days: 7,
    accuracy: r.accuracy,
    read: r.read,
  };
}

export function sampleReportHistory(): ChildSummaryHistoryResponse {
  const locale = getLanguage();
  return { reports: REPORTS.map((r) => historyItem(r, locale)) };
}

const DAILY_MINS = [18, 25, 0, 30, 22, 15, 0, 28, 35, 20, 0, 26, 31, 24];

export function sampleReport(reportId: string): ChildSummaryResponse {
  const locale = getLanguage();
  const r = REPORTS.find((x) => x.id === reportId) ?? REPORTS[0];
  const item = historyItem(r, locale);
  const t = i18n.getFixedT(locale, 'hub');
  const rec = (c: (typeof CHAPTERS)['percentages' | 'ratios']) => ({
    chapterNum: c.num,
    name: c[locale],
    chapterId: c.id,
    syllabus: 'KSSM',
    grade: 'Form 2',
  });
  return {
    id: r.id,
    title: item.title,
    appUsage: { activeDays: 5, totalDays: 7, totalMins: 112, dailyMins: DAILY_MINS },
    overview: t('sampleReportOverview', { childName: CHILD_NAME }),
    strengths: [t('sampleReportStrength1'), t('sampleReportStrength2')],
    improvements: [t('sampleReportImprovement1')],
    recommendations: [rec(CHAPTERS.percentages), rec(CHAPTERS.ratios)],
    accuracy: r.accuracy,
    generated_at: item.generated_at,
    period_start: item.period_start,
    period_end: item.period_end,
    period_days: 7,
    can_regenerate: false,
    cooldown_until: null,
    from_cache: true,
    read: r.read,
  };
}

// ── Homework ────────────────────────────────────────────────────────────────

const ASSIGNMENTS: {
  id: string;
  chapter: (typeof CHAPTERS)[keyof typeof CHAPTERS];
  status: AssignmentSummary['status'];
  assigned: number;
  started: number | null;
  completed: number | null;
  correct: number | null;
}[] = [
  {
    id: 'sample-hw-1',
    chapter: CHAPTERS.fractions,
    status: 'completed',
    assigned: 3,
    started: 1,
    completed: 1,
    correct: 8,
  },
  {
    id: 'sample-hw-2',
    chapter: CHAPTERS.ratios,
    status: 'started',
    assigned: 4,
    started: 3,
    completed: null,
    correct: null,
  },
  {
    id: 'sample-hw-3',
    chapter: CHAPTERS.algebra,
    status: 'pending',
    assigned: 2,
    started: null,
    completed: null,
    correct: null,
  },
  {
    id: 'sample-hw-4',
    chapter: CHAPTERS.percentages,
    status: 'completed',
    assigned: 15,
    started: 13,
    completed: 13,
    correct: 6,
  },
];

export function sampleAssignments(): AssignmentSummary[] {
  const locale = getLanguage();
  return ASSIGNMENTS.map((a) => ({
    assignment_id: a.id,
    link_id: SAMPLE_LINK_ID,
    chapter_id: a.chapter.id,
    chapter_title: a.chapter[locale],
    chapter_title_ms: a.chapter.ms,
    status: a.status,
    assigned_at: ago(a.assigned, 18),
    started_at: a.started != null ? ago(a.started, 20, 16) : null,
    completed_at: a.completed != null ? ago(a.completed, 20, 30) : null,
    student_id: SAMPLE_LINK_ID,
    student_name: CHILD_NAME,
    correct_count: a.correct,
    total_questions: a.correct != null ? 10 : null,
  }));
}

// [question, child's answer, correct answer]; a matching pair is a right answer.
const QUESTIONS: Record<'fractions' | 'percentages', [string, string, string][]> = {
  fractions: [
    ['Simplify $\\frac{12}{18}$', '2/3', '2/3'],
    ['$\\frac{3}{4} + \\frac{1}{8} = ?$', '7/8', '7/8'],
    ['What is $\\frac{2}{5}$ of 45?', '18', '18'],
    ['Write 0.35 as a fraction in its simplest form', '35/100', '7/20'],
    ['$1\\frac{1}{2} \\times \\frac{2}{3} = ?$', '1', '1'],
    ['Which is larger: $\\frac{5}{8}$ or $\\frac{3}{5}$?', '5/8', '5/8'],
    ['$\\frac{5}{6} - \\frac{1}{4} = ?$', '4/2', '7/12'],
    ['Express $3\\frac{3}{4}$ as an improper fraction', '15/4', '15/4'],
    ['$\\frac{1}{3}$ of a number is 14. What is the number?', '42', '42'],
    ['$\\frac{2}{3} \\div 4 = ?$', '1/6', '1/6'],
  ],
  percentages: [
    ['What is 20% of 150?', '30', '30'],
    ['Write $\\frac{3}{5}$ as a percentage', '60%', '60%'],
    ['A price rises from RM40 to RM50. What is the percentage increase?', '20%', '25%'],
    ['What is 15% of 80?', '12', '12'],
    ['Write 0.45 as a percentage', '45%', '45%'],
    ['A price falls from RM80 to RM60. What is the percentage decrease?', '20%', '25%'],
    ['35 is what percentage of 140?', '25%', '25%'],
    ['Increase 200 by 10%', '210', '220'],
    ['What is 5% of 360?', '18', '18'],
    ['A class of 40 has 18 girls. What percentage are boys?', '18%', '55%'],
  ],
};

export function sampleAssignmentResult(assignmentId: string): AssignmentResultResponse {
  const a = ASSIGNMENTS.find((x) => x.id === assignmentId) ?? ASSIGNMENTS[0];
  const summary = sampleAssignments().find((x) => x.assignment_id === a.id)!;
  const rows = QUESTIONS[a.chapter === CHAPTERS.percentages ? 'percentages' : 'fractions'];
  return {
    assignment_id: a.id,
    chapter_title: summary.chapter_title,
    chapter_title_ms: summary.chapter_title_ms,
    student_name: CHILD_NAME,
    status: summary.status,
    assigned_at: summary.assigned_at,
    started_at: summary.started_at,
    completed_at: summary.completed_at,
    // The shapes HomeworkResultPage's buildRows() reads: one text step per
    // question, and a result per question index.
    question_set: rows.map(([question, , correct]) => ({
      question_text: question,
      steps: [{ step_index: 0, input_type: 'text', options: [], accepted_answers: [correct] }],
    })),
    results: rows.map(([, answered, correct], i) => ({
      question_index: i,
      node_id: '',
      complete: true,
      all_correct: answered === correct,
      steps: [
        { step_index: 0, selected_index: -1, text_answer: answered, correct: answered === correct },
      ],
    })),
    correct_count: rows.filter(([, answered, correct]) => answered === correct).length,
    total_questions: rows.length,
  };
}

// ── Activity ────────────────────────────────────────────────────────────────

export function sampleActivity(): ChildActivityResponse {
  const locale = getLanguage();
  const item = (
    n: number,
    chapter: (typeof CHAPTERS)[keyof typeof CHAPTERS],
    days: number,
    hour: number,
    minute: number,
    correct: number,
    completed = true,
    homework = false
  ) => ({
    practice_id: `sample-p${n}`,
    chapter_id: chapter.id,
    chapter_title: chapter[locale],
    correct_count: correct,
    total_questions: 10,
    completed,
    is_homework: homework,
    created_at: ago(days, hour, minute - 12),
    completed_at: completed ? ago(days, hour, minute) : null,
  });
  return {
    activity: [
      item(1, CHAPTERS.ratios, 0, 16, 12, 7),
      item(2, CHAPTERS.ratios, 0, 15, 48, 3, false),
      item(3, CHAPTERS.fractions, 1, 20, 30, 8, true, true),
      item(4, CHAPTERS.fractions, 1, 19, 55, 9),
      item(5, CHAPTERS.percentages, 3, 21, 5, 5),
      item(6, CHAPTERS.algebra, 4, 20, 40, 6),
    ],
  };
}
